import { Suspense, useMemo, useCallback, useRef } from "react";
import { useGLTF } from "@react-three/drei";
import { ThreeEvent } from "@react-three/fiber";
import * as THREE from "three";
import { RoofModelProps } from "../types";
import { RoofTreeRow, PART_TYPE_IDS } from "../../../models";
import {
  HOVER_EMISSIVE_INTENSITY,
  SELECTION_EMISSIVE_INTENSITY,
  EMPTY_POSITION_COLOR,
} from "../constants";
import {
  Viewer3DMaterials,
  Viewer3DEmissiveColors,
  Viewer3DGeometry,
  Viewer3DSettings,
} from "../../../../../shared/theme";

// Gray color for empty positions (no part installed)
const EMPTY_COLOR = new THREE.Color(EMPTY_POSITION_COLOR);

// Path to the GLB model
const ROOF_MODEL_PATH = new URL("../assets/RoofAssembly.glb", import.meta.url)
  .href;

// Scale factor - GLB is already in meters (~7m wide), scale down to fit view (~1.5 units)
const MODEL_SCALE = Viewer3DGeometry.MODEL_SCALE;

// Mapping from GLB node names to position IDs (roof positions, not part types)
// This allows unified part types while keeping GLB node selection unique per position
// Based on the actual GLB structure from "RoofAssembly.glb"
const GLB_NODE_TO_POSITION_ID: Record<string, number> = {
  Roof_Frame1: 1, // Spider
  "4th_Hole_Panel1": 2, // 4th Hole Panel/Skirt
  "4th_Hole_Elbow1": 3, // 4th Hole Elbow
  Elbow_Shield1: 12, // Elbow Shield
  "55_Deg_Panel1": 4, // South 55 position
  "55_Deg_Panel_w_Hole1": 5, // South 60 position
  Water_Skirt2: 6, // South Skirt
  Delta_Assembly1: 7, // Delta Ring
  "60_Deg_Panel1": 8, // Center 60
  "55_Deg_Panel2": 9, // North 60 position
  Water_Skirt1: 10, // North Skirt
  "55_Deg_Panel_w_Hole2": 11, // North 55 position
};

// Mapping from position IDs to GLB node names (for selection/hover highlighting)
const POSITION_TO_GLB_NODES: Record<number, string[]> = {
  1: ["Roof_Frame1"], // Spider
  2: ["4th_Hole_Panel1"], // 4th Hole Panel/Skirt
  3: ["4th_Hole_Elbow1"], // 4th Hole Elbow
  4: ["55_Deg_Panel1"], // South 55 position
  5: ["55_Deg_Panel_w_Hole1"], // South 60 position
  6: ["Water_Skirt2"], // South Skirt
  7: ["Delta_Assembly1"], // Delta Ring
  8: ["60_Deg_Panel1"], // Center 60
  9: ["55_Deg_Panel2"], // North 60 position
  10: ["Water_Skirt1"], // North Skirt
  11: ["55_Deg_Panel_w_Hole2"], // North 55 position
  12: ["Elbow_Shield1"], // Elbow Shield
};

interface RoofModelInnerProps {
  roofData: RoofModelProps["roofData"];
  selectedLocationId: string | null;
  hoveredLocationId: string | null;
  onSelectLocation: (row: RoofTreeRow | null) => void;
  onHoverLocation: (locationId: string | null) => void;
}

const RoofModelInner = ({
  roofData,
  selectedLocationId,
  hoveredLocationId,
  onSelectLocation,
  onHoverLocation,
}: RoofModelInnerProps) => {
  const { scene } = useGLTF(ROOF_MODEL_PATH);
  const groupRef = useRef<THREE.Group>(null);

  // Get locationId from PositionId
  const getLocationId = useCallback(
    (positionId: number) => {
      return `location-${roofData.roofId}-${positionId}`;
    },
    [roofData.roofId],
  );

  // Find location data by PositionId (unique per position)
  const getLocationByPositionId = useCallback(
    (positionId: number): RoofTreeRow | null => {
      return (
        roofData.locations.find((loc) => loc.PositionId === positionId) ?? null
      );
    },
    [roofData.locations],
  );

  // Find PositionId from GLB node name (traverses up to find parent)
  const getPositionIdFromObject = useCallback(
    (object: THREE.Object3D): number | null => {
      let current: THREE.Object3D | null = object;
      while (current) {
        if (
          current.name &&
          GLB_NODE_TO_POSITION_ID[current.name] !== undefined
        ) {
          return GLB_NODE_TO_POSITION_ID[current.name];
        }
        current = current.parent;
      }
      return null;
    },
    [],
  );

  // Create a deep clone of the scene with interactive materials
  // Store original colors in userData for restoration
  const clonedScene = useMemo(() => {
    const clone = scene.clone(true);

    // Deep clone materials and store original colors
    clone.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (Array.isArray(child.material)) {
          child.material = child.material.map((m) => {
            const cloned = m.clone();
            if (cloned instanceof THREE.MeshStandardMaterial) {
              cloned.userData.originalColor = cloned.color.clone();
            }
            return cloned;
          });
        } else if (child.material) {
          const cloned = child.material.clone();
          if (cloned instanceof THREE.MeshStandardMaterial) {
            cloned.userData.originalColor = cloned.color.clone();
          }
          child.material = cloned;
        }
      }
    });

    return clone;
  }, [scene]);

  // Check if a position has an installed part
  const hasInstalledPart = useCallback(
    (positionId: number): boolean => {
      const location = roofData.locations.find(
        (loc) => loc.PositionId === positionId,
      );
      return location?.HasAssignedPart ?? false;
    },
    [roofData.locations],
  );

  // Helper to find location by locationId and get its PositionId
  const getPositionIdFromLocationId = useCallback(
    (locationId: string | null): number | null => {
      if (!locationId) return null;

      const location = roofData.locations.find(
        (loc) => `location-${roofData.roofId}-${loc.PositionId}` === locationId,
      );
      return location?.PositionId ?? null;
    },
    [roofData.locations, roofData.roofId],
  );

  // Update materials based on hover/selection state and installation status
  useMemo(() => {
    if (!clonedScene) return;

    // Get selected and hovered PositionIds from the actual location data
    const selectedPositionId = getPositionIdFromLocationId(selectedLocationId);
    const hoveredPositionId = getPositionIdFromLocationId(hoveredLocationId);

    // Get all node names that should be selected/hovered
    const selectedNodes = selectedPositionId
      ? (POSITION_TO_GLB_NODES[selectedPositionId] ?? [])
      : [];
    const hoveredNodes = hoveredPositionId
      ? (POSITION_TO_GLB_NODES[hoveredPositionId] ?? [])
      : [];

    // Update all meshes
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        // Find which position this mesh belongs to
        const positionId = getPositionIdFromObject(child);

        // Determine selection/hover/installed state
        const nodeNames = positionId
          ? (POSITION_TO_GLB_NODES[positionId] ?? [])
          : [];
        const isSelected = nodeNames.some((name) =>
          selectedNodes.includes(name),
        );
        const isHovered = nodeNames.some((name) => hoveredNodes.includes(name));
        // If positionId is null (unmatched mesh), treat as not installed (gray)
        const isInstalled = positionId !== null && hasInstalledPart(positionId);

        // Apply color and emissive effect based on state
        const materials = Array.isArray(child.material)
          ? child.material
          : [child.material];

        materials.forEach((mat) => {
          if (mat instanceof THREE.MeshStandardMaterial) {
            // Apply color based on installation status
            if (isInstalled) {
              // Use original GLB color for installed parts
              const originalColor = mat.userData.originalColor as
                | THREE.Color
                | undefined;
              if (originalColor) {
                mat.color.copy(originalColor);
              }
            } else {
              // Use gray color for empty positions
              mat.color.copy(EMPTY_COLOR);
              // Normalize material properties for consistent gray appearance
              mat.metalness = Viewer3DMaterials.EMPTY_METALNESS;
              mat.roughness = Viewer3DMaterials.EMPTY_ROUGHNESS;
            }

            // Apply emissive effect based on selection/hover state
            if (isSelected) {
              mat.emissive = new THREE.Color(Viewer3DEmissiveColors.HIGHLIGHT);
              mat.emissiveIntensity = SELECTION_EMISSIVE_INTENSITY;
            } else if (isHovered) {
              mat.emissive = new THREE.Color(Viewer3DEmissiveColors.HIGHLIGHT);
              mat.emissiveIntensity = HOVER_EMISSIVE_INTENSITY;
            } else {
              mat.emissive = new THREE.Color(Viewer3DEmissiveColors.DEFAULT);
              mat.emissiveIntensity =
                Viewer3DSettings.DEFAULT_EMISSIVE_INTENSITY;
            }
          }
        });
      }
    });
  }, [
    clonedScene,
    selectedLocationId,
    hoveredLocationId,
    getPositionIdFromObject,
    getPositionIdFromLocationId,
    hasInstalledPart,
  ]);

  // Handle click on the model
  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      event.stopPropagation();

      const positionId = getPositionIdFromObject(event.object);
      if (positionId !== null) {
        const location = getLocationByPositionId(positionId);
        onSelectLocation(location);
      }
    },
    [getPositionIdFromObject, getLocationByPositionId, onSelectLocation],
  );

  // Handle pointer over (hover)
  const handlePointerOver = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      document.body.style.cursor = "pointer";

      const positionId = getPositionIdFromObject(event.object);
      if (positionId !== null) {
        const location = getLocationByPositionId(positionId);
        if (location && location.PositionId) {
          onHoverLocation(getLocationId(location.PositionId));
        }
      }
    },
    [
      getPositionIdFromObject,
      getLocationByPositionId,
      getLocationId,
      onHoverLocation,
    ],
  );

  // Handle pointer out (hover end)
  const handlePointerOut = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      document.body.style.cursor = "auto";
      onHoverLocation(null);
    },
    [onHoverLocation],
  );

  return (
    <group ref={groupRef}>
      <primitive
        object={clonedScene}
        scale={MODEL_SCALE}
        onClick={handleClick}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      />
    </group>
  );
};

export const RoofModel = ({
  roofData,
  selectedLocationId,
  hoveredLocationId,
  onSelectLocation,
  onHoverLocation,
}: RoofModelProps) => {
  return (
    <Suspense
      fallback={
        <mesh>
          <boxGeometry
            args={[
              Viewer3DGeometry.FALLBACK_BOX_SIZE,
              Viewer3DGeometry.FALLBACK_BOX_SIZE,
              Viewer3DGeometry.FALLBACK_BOX_SIZE,
            ]}
          />
          <meshStandardMaterial color="red" />
        </mesh>
      }
    >
      <RoofModelInner
        roofData={roofData}
        selectedLocationId={selectedLocationId}
        hoveredLocationId={hoveredLocationId}
        onSelectLocation={onSelectLocation}
        onHoverLocation={onHoverLocation}
      />
    </Suspense>
  );
};

// Preload the GLB model
useGLTF.preload(ROOF_MODEL_PATH);
